// We include the polyfill here.
// This is not ideal since it may result in multiple inclusions.
// But at least it's safe this way that there's a polyfill for IE.
import "promise-polyfill/src/polyfill";

import {
	decode,
} from "base64-arraybuffer";
import ResizeObserver from "resize-observer-polyfill";
import {
	Box3,
	BufferGeometry,
	Cache,
	Color,
	InterleavedBuffer,
	InterleavedBufferAttribute,
	Light,
	Line,
	Mesh,
	Object3D,
	PerspectiveCamera,
	Scene,
	sRGBEncoding,
	Vector3,
	WebGLRenderer,
} from "three";
import {
	LineMaterial,
} from "three/examples/jsm/lines/LineMaterial";
import {
	LineSegments2,
} from "three/examples/jsm/lines/LineSegments2";
import {
	LineSegmentsGeometry,
} from "three/examples/jsm/lines/LineSegmentsGeometry";
import {
	DRACOLoader,
} from "three/examples/jsm/loaders/DRACOLoader";
import {
	GLTF,
	GLTFLoader,
} from "three/examples/jsm/loaders/GLTFLoader";

//////////////
/////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////

/////////

import {
	DefaultGltfSceneConfig,
} from "./default_gltf_scene_config";
import {
	GltfSceneConfig,
} from "./gltf_scene_config";
import {
	expandArrowsAltSolidSvg,
	timesSvg,
} from "./resources";
import {
	Web3dModel,
} from "./web3d_model";
import {
	Web3dScene,
} from "./web3d_scene";

function createSvgElement(svg: string): HTMLElement {
	const dp = new DOMParser();
	return dp.parseFromString(svg, "image/svg+xml").documentElement;
}

function toLineSegmentsGeometry(bufferGeometry: BufferGeometry) {
	const lg = new LineSegmentsGeometry();

	const a = bufferGeometry.attributes;
	if (a.position === undefined) {
		return;
	}
	const position = a.position;
	if (!(position instanceof InterleavedBufferAttribute)) {
		return;
	}

	// It would be great if everything here could be replaced by the code further down.
	// This doesn't work as of now as mentioned here https://github.com/mrdoob/three.js/issues/15585
	const data = position.data;
	if (!(data instanceof InterleavedBuffer)) {
		return;
	}
	const stride = data.stride;
	const indexAttribute = bufferGeometry.index;
	if (indexAttribute === null) {
		return;
	}
	const positions = [];
	for (let index = 0; index !== indexAttribute.array.length; ++index) {
		const i = indexAttribute.array[index] * stride;
		const x = data.array[i];
		const y = data.array[i + 1];
		const z = data.array[i + 2];
		positions.push(x, y, z);
	}

	lg.setPositions(positions);

	// Alternative "better" code not working.
	/*
	lg.setAttribute("position", a.position);
	lg.setIndex(bufferGeometry.index);
	*/

	lg.userData = bufferGeometry.userData;
	return lg;
}

function changeLineElements(object: Object3D, width: number, height: number) {
	const lines: Array<Line> = [];
	object.traverse((o) => {
		if (o instanceof Line)
			lines.push(o);
	});
	const material = new LineMaterial({
		color: 0,
		linewidth: 2,
		dashed: false,
		// Do not set vertexColors here, it destroys anti-aliasing
		// vertexColors : THREE.VertexColors,
		// Polygon offset does not work with these lines, unfortunately.
		// polygonOffset : true,
		// polygonOffsetFactor : -1.,
		// polygonOffsetUnits : 1.
	});
	material.resolution.set(width, height);
	lines.forEach(line => {
		const geo = line.geometry;
		if (geo instanceof BufferGeometry) {
			const lineSegments2 = new LineSegments2(toLineSegmentsGeometry(geo), material);
			const parent = line.parent;
			if (parent !== null) {
				parent.remove(line);
				parent.add(lineSegments2);
			}
		}
	});
}

function createLoader() {
	const dracoVersion = "1.4.1";
	const loader = new GLTFLoader();
	const dracoLoader = new DRACOLoader();
	const dracoUrl = "https://www.gstatic.com/draco/versioned/decoders/";
///////////////
//////////////////////
//////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////
////////////////////////////////////////////////
///////////////////////////////////////////////////////////////
//////////
	dracoLoader.setDecoderPath(dracoUrl + dracoVersion + "/");
	dracoLoader.preload();
	loader.setDRACOLoader(dracoLoader);
	return loader;
}

export default class Web3dRenderer {
	private container: HTMLElement;
	private scene?: Web3dScene;
	private config?: GltfSceneConfig;
	private configCallback: (scene: Web3dScene) => GltfSceneConfig;
	private renderer: WebGLRenderer;
	private loader: GLTFLoader;
	private body: HTMLElement = document.createElement("div");
	private modalWindow?: HTMLElement;
	private showUi: boolean = false;
	private uiElement: HTMLElement = document.createElement("div");
	private input: ArrayBuffer | string = "";

	private skipNextOnElementResize = true;

	constructor(container: HTMLElement, showUi: boolean=false, configCallback: (scene: Web3dScene) => GltfSceneConfig = ((_s: Web3dScene) => new DefaultGltfSceneConfig())) {
		this.container = container;
		this.showUi = showUi;
		this.configCallback = configCallback;
		this.renderer = this.initRenderer();
		this.renderer.setSize(this.rendererWidth(), this.rendererHeight());
		this.initBody();
		this.loader = createLoader();
		this.animate = (_timestamp?: DOMHighResTimeStamp) => {
			if (this.config !== undefined && this.config.animate !== undefined) {
				this.config.animate();
			}
			this.render();
			requestAnimationFrame(this.animate);
		};
	}

	/**
	 * Display binary encoded gltf content.
	 *
	 * This function accepts either an ArrayBuffer or a string.
	 * If it's an ArrayBuffer, it is expected to contain a binary GLTF file.
	 * If the input is a string, it is assumed to be a binary GLTF file encoded with base64.
	 */
	public display(input: ArrayBuffer | string) {
		this.input = input;
		this.initConfig();
		if (this.scene === undefined) {
			throw Error("initConfig failed to provide a scene");
		}
		const buffer = typeof input === "string" ? decode(input) : input;

		this.loader.parse(buffer,
			"",
			(data: GLTF) => {
				const object = data.scene;
				object.traverse((node: Object3D) => {
					if (node instanceof Mesh || node instanceof Light) {
						node.castShadow = false;
					}
				});
				changeLineElements(object, this.rendererWidth(), this.rendererHeight());

				this.setCameraPosition(object);
				if (this.scene !== undefined) {
					this.scene.scene.add(object);
				}

				const m = new Web3dModel(object, data.userData);
				if (this.config !== undefined && this.config.modelLoaded !== undefined) {
					this.config.modelLoaded(m);
				}
				this.onElementResize();
				this.animate();
			},
			(error: ErrorEvent) => {
				// TODO: should this display an error message to the user?
				console.error(error);
			});
	}

	public setClassName(name: string) {this.body.className = name;}

	private width() {return this.container.clientWidth === 0 ? window.innerWidth : this.container.clientWidth;}
	private height() {return this.container.clientHeight === 0 ? window.innerHeight : this.container.clientHeight;}
	private uiSize() {return 0.04 * this.width();}
	private rendererWidth() {return this.width();}
	private rendererHeight() {return this.height();}
	private sizeStyle(size: number) {return size + "px";}

	private initBody() {
		this.setClassName("web3d-renderer");
		this.body.style.width = this.sizeStyle(this.width());
		this.body.style.height = this.sizeStyle(this.height());
		this.body.appendChild(this.renderer.domElement);
		this.container.appendChild(this.body);
		if (this.showUi) {
			this.initUi();
		}
	}

	private initConfig() {
		const cameraAndScene = this.initCameraAndScene();
		this.scene = new Web3dScene(this.renderer, cameraAndScene.camera, cameraAndScene.scene);
		this.config = this.configCallback(this.scene);
		if (this.config.initScene !== undefined) {
			this.config.initScene(this.scene);
		}
	}

	private initRenderer(): WebGLRenderer {
		const renderer = new WebGLRenderer({antialias: true});
		renderer.setPixelRatio(window.devicePixelRatio);
		renderer.outputEncoding = sRGBEncoding;
		renderer.domElement.className += " webgl-renderer";
		new ResizeObserver(() => this.onElementResize()).observe(this.container);
		return renderer;
	}

	private initUi() {
		this.uiElement.style.display = "none";
		this.uiElement.className = "ui-column";
		this.uiElement.style.width = this.sizeStyle(this.uiSize());
		this.uiElement.style.height = this.sizeStyle(this.uiSize());
		this.uiElement.style.position = "relative";
		this.uiElement.style.left = this.sizeStyle(-this.uiSize() - 2);
		const expandButton = document.createElement("button");
		expandButton.className = "expand-button";
		expandButton.style.width = this.sizeStyle(this.uiSize() - 2);
		expandButton.style.height = this.sizeStyle(this.uiSize() - 2);
		expandButton.addEventListener("click", () => this.openModalWindow());
		const expandIcon = document.createElement("div");
		expandIcon.className = "expand-icon";
		expandIcon.appendChild(createSvgElement(expandArrowsAltSolidSvg));
		expandButton.appendChild(expandIcon);
		this.uiElement.appendChild(expandButton);
		this.body.appendChild(this.uiElement);
	}

	private openModalWindow() {
		this.modalWindow = document.createElement("div");
		this.modalWindow.className = "modal-window";
		const modalWindowDialog = document.createElement("div");
		modalWindowDialog.className = "modal-window-dialog";
		const modalWindowClose = document.createElement("button");
		modalWindowClose.className = "modal-window-close";
		const closeIcon = document.createElement("div");
		closeIcon.className = "modal-window-close-icon";
		closeIcon.style.width = this.sizeStyle(this.uiSize() - 2);
		closeIcon.style.height = this.sizeStyle(this.uiSize() - 2);
		closeIcon.appendChild(createSvgElement(timesSvg));
		modalWindowClose.appendChild(closeIcon);
		modalWindowClose.addEventListener("click", () => this.closeModalWindow());
		modalWindowDialog.appendChild(modalWindowClose);
		const rendererWindow = document.createElement("div");
		modalWindowDialog.appendChild(rendererWindow);
		this.modalWindow.appendChild(modalWindowDialog);
		this.body.appendChild(this.modalWindow);

		rendererWindow.style.width = this.sizeStyle(window.innerWidth * 0.9);
		rendererWindow.style.height = this.sizeStyle(window.innerHeight * 0.9);
		const r = new Web3dRenderer(rendererWindow, false);
		r.setClassName("modal-window-renderer");
		r.display(this.input);
	}

	private closeModalWindow() {
		if (!this.modalWindow) {
			throw Error("Missing modal window");
		}
		this.body.removeChild(this.modalWindow);
		this.modalWindow = undefined;
	}

	private initCameraAndScene() {
		const scene = new Scene();
		scene.background = new Color(0xf0f0f0);

		const camera = new PerspectiveCamera(90, this.container.offsetWidth / this.container.offsetHeight, 0.01, 100000);
		scene.add(camera);

		return {scene: scene, camera: camera};
	}

	private onElementResize() {
		// this.scene.renderer.setSize triggers another onElementResize.
		// So we saved the size we resized to and only resized in case it changed.
		// However, this.scene.renderer.setSize actually increases the height by 4px.
		// So this again doesn't work.
		// Therefore, after a call to setSize, we make the next call to onElementResize return early.
		// This might race ever so slightly with UI interaction but should be fine.
		if (this.skipNextOnElementResize) {
			this.skipNextOnElementResize = false;
			return;
		} else {
			this.skipNextOnElementResize = true;
		}
		if (this.scene === undefined) {
			return;
		}
		const width = this.rendererWidth();
		const height = this.rendererHeight();
		if (this.scene.camera instanceof PerspectiveCamera) {
			this.scene.camera.aspect = width / height;
		} else {
			this.scene.camera.left = -.5 * width;
			this.scene.camera.right = .5 * width;
			this.scene.camera.bottom = -.5 * height;
			this.scene.camera.top = .5 * height;
		}
		this.scene.camera.updateProjectionMatrix();
		this.scene.renderer.setSize(width, height);
		if (this.config !== undefined && this.config.resize !== undefined) {
			this.config.resize(width, height, this.renderer.pixelRatio);
		}
	}

	// Note: This has to be an arrow function so we have correct access to "this"
	private animate = (_timestamp?: DOMHighResTimeStamp) => {};

	private render() {
		if (this.scene !== undefined && this.scene.scene !== undefined) {
			if (this.config !== undefined && this.config.beforeRender !== undefined) {
				this.config.beforeRender(this.scene.scene, this.scene.camera);
			}
			this.scene.renderer.render(this.scene.scene, this.scene.camera);
			this.uiElement.style.display = "unset";
		}
	}

	// Set the camera position so that an object is nicely in focus
	private setCameraPosition(object: Object3D) {
		if (this.scene !== undefined && this.scene.camera instanceof PerspectiveCamera) {
			const computePerspectiveDistance = (boundingBox: Box3, fov: number, aspect: number) => {
				const size = new Vector3();
				boundingBox.getSize(size);
				const diam = size.length();

				const alpha = fov * Math.PI / 180.;
				const d0 = .5 * diam / Math.tan(.5 * alpha) * 1.25;

				console.assert(aspect > 0, "invalid aspect: " + aspect);
				const d1 = d0 / aspect;

				const minDistance = 1.;

				return Math.max(d0, d1, minDistance);
			};

			const aspect = this.scene.camera.aspect;
			const fov = this.scene.camera.fov;

			const bbox = new Box3().setFromObject(object);
			const center = new Vector3();
			bbox.getCenter(center);

			const distance = computePerspectiveDistance(bbox, fov, aspect);

			if (this.config !== undefined && this.config.controls !== undefined) {
				this.config.controls.distance = distance;
				this.config.controls.target = center;
			}
		}
	}
}
